/**
 * AdminMenu — persistent left rail for the Administration surface.
 * Design contract §5.5.
 *
 * Updated WP-SL-V5: added Orders menu item (route /admin/orders).
 */

import { useConfig } from '../config/ConfigContext';
import type { InstanceMode } from '../config/schema';
import { routesForMode } from '../shell/routes';
import type { Route } from '../shell/Router';

export type AdminRoute =
  | '/admin'
  | '/admin/sub-clients'
  | '/admin/audit-log'
  | '/admin/subscriptions'
  | '/admin/orders'
  | '/admin/sandbox-provisioning';

interface AdminMenuProps {
  activePath: Route;
  onNavigate: (path: AdminRoute) => void;
}

export interface MenuItem {
  label: string;
  path: AdminRoute;
  isPlaceholder: boolean;
}

// Exported so NavRail can render the same sub-items inline when
// "Administration" is expanded (brief-13-shell-nav-rail follow-up) — single
// source of truth, no duplicated literal list to drift out of sync.
export const MENU_ITEMS: MenuItem[] = [
  { label: 'Tenants',        path: '/admin',                isPlaceholder: true  },
  { label: 'Sub-clients',    path: '/admin/sub-clients',    isPlaceholder: false },
  { label: 'Auth',           path: '/admin',                isPlaceholder: true  },
  { label: 'Orders',         path: '/admin/orders',         isPlaceholder: false },
  { label: 'Subscriptions',  path: '/admin/subscriptions',  isPlaceholder: false },
  { label: 'Audit log',      path: '/admin/audit-log',      isPlaceholder: false },
  { label: 'Sandbox provisioning', path: '/admin/sandbox-provisioning', isPlaceholder: false },
  { label: 'Runtime config', path: '/admin',                isPlaceholder: true  },
];

// Shared with NavRail so the rail's expanded sub-items highlight the same
// entry AdminMenu would — a path like '/admin' is intentionally ambiguous
// between Tenants/Auth/Runtime config (Auth & Runtime config are still
// placeholders), so match by label, not just path.
export function isMenuItemActive(item: MenuItem, activePath: Route): boolean {
  switch (item.label) {
    case 'Sub-clients': return activePath === '/admin/sub-clients';
    case 'Subscriptions': return activePath === '/admin/subscriptions';
    case 'Orders': return activePath === '/admin/orders';
    case 'Audit log': return activePath === '/admin/audit-log';
    case 'Sandbox provisioning': return activePath === '/admin/sandbox-provisioning';
    case 'Tenants': return activePath === '/admin';
    default: return false;
  }
}

/**
 * The mode-filtered item list. Exported so NavRail’s expanded Administration
 * sub-list shows exactly what AdminMenu would on the same deployment — a tenant
 * broker must not see a sub-item for a route it does not register
 * (decision-2026-07-25-broker-mode-split).
 */
export function menuItemsForMode(mode: InstanceMode): MenuItem[] {
  const registered = routesForMode(mode) as string[];
  return MENU_ITEMS.filter((item) => item.isPlaceholder || registered.includes(item.path));
}

export default function AdminMenu({ activePath, onNavigate }: AdminMenuProps) {
  // decision-2026-07-25-broker-mode-split: the menu is derived from the mode's route
  // table, so a tenant-mode instance renders no entry for a route it does not
  // register. Placeholder items point at '/admin', which every broker mode has.
  const config = useConfig();
  const items = menuItemsForMode(config.instance.mode);

  return (
    <aside
      className="dp-shell__sidebar"
      style={{
        background: 'var(--bg-recessed)',
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: 'var(--border-subtle)',
        padding: 'var(--sp-6) 0',
        flexShrink: 0,
      }}
      aria-label="Administration menu"
    >
      <p
        className="dp-eyebrow"
        style={{ padding: '0 var(--sp-5)', marginBottom: 'var(--sp-3)', margin: '0 0 var(--sp-3)' }}
      >
        Administration
      </p>

      <ul role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item, idx) => {
          const isActive = isMenuItemActive(item, activePath);

          return (
            <li key={`${item.label}-${idx}`}>
              <button
                onClick={() => onNavigate(item.path)}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  width: '100%',
                  padding: 'var(--sp-2) var(--sp-5)',
                  background: isActive ? 'var(--bg-surface)' : 'transparent',
                  boxShadow: isActive ? 'var(--shadow-1)' : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  fontWeight: 500,
                  color: isActive ? 'var(--fg-1)' : 'var(--fg-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background var(--dur-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background = 'var(--hover-veil)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {item.label}
                {item.isPlaceholder && (
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)',
                    color: 'var(--fg-4)', background: 'var(--dp-canvas-300)',
                    borderRadius: 'var(--radius-sm)', padding: '1px 5px',
                  }}>
                    soon
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
